import React, { useCallback } from 'react';
import { Divider, Flex } from '@withjoy/joykit';
import { Coordinates, AccommodationListingDTO } from '@apps/accommodations/AccommodationsConsole.types';
import { AccommodationsConsoleState } from '@apps/accommodations/AccommodationsConsole.types';
import { useHotelRoutePaths, useHotelBookingRoutePaths, UtmParameters } from '@apps/guest/routes/HotelBooking/HotelBooking.routes';
import { useEventInfo } from '@shared/utils/eventInfo';

import { ListingPhotos } from './ListingPhotos';
import { HeaderBlock } from './HeaderBlock';
import { TravelLogistics } from './TravelLogistics';
import { CostBreakdown } from './CostBreakdown';
import { HotelCTA } from './HotelCTA';
import { CouplesPick } from './CouplesPick';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';

const Container = ({ children, id }: { children: React.ReactNode; id?: string }) => {
  return (
    <Flex id={id} flexDirection={{ _: 'column', md: 'row' }} columnGap={4}>
      {children}
    </Flex>
  );
};

const ContentPanel = ({ children }: { children: React.ReactNode }) => {
  return (
    <Flex flexDirection="column" width={'100%'} gap={{ _: 4, md: 0 }} paddingY={{ _: 4, md: 0 }}>
      {children}
    </Flex>
  );
};

const LogisticsAndCostBlock = ({ children }: { children: React.ReactNode }) => {
  return (
    <Flex
      flexDirection={{ _: 'column', md: 'row' }}
      gap={{ _: 4, md: 0 }}
      marginTop={{ _: 0, md: 6 }}
      justifyContent={{ _: 'flex-start', md: 'space-between' }}
      alignItems={{ _: 'none', md: 'center' }}
      width={{ _: '100%', md: 'auto' }}
    >
      {children}
    </Flex>
  );
};

// TODO:
// - use translation values
const ListingEntry = (props: { venueCoordinates: Coordinates | null; listing: AccommodationListingDTO; state: AccommodationsConsoleState; noOfNights: number }) => {
  const { listing, venueCoordinates, state, noOfNights } = props;
  const isMobileOrTablet = useIsMobileOrTablet();
  const { eventHandle } = useEventInfo();
  const hotelRoute = useHotelRoutePaths();
  const { buildPath, roomblock } = useHotelBookingRoutePaths();

  const getRedirectUrl = useCallback(() => {
    const paramsData = {
      utm_medium: 'web',
      utm_source: 'website_accommodations_console_page',
      utm_campaign: 'see_availability',
      ...(state.searchDates?.start && {
        startDate: state.searchDates.start
      }),
      ...(state.searchDates?.end && {
        endDate: state.searchDates.end
      }),
      ...(state.occupancies?.occupancies?.length && {
        rooms: state.occupancies.occupancies.map(room => `a${room.adults}${room.childrenAges?.length ? `:c${room.childrenAges.join(':c')}` : ''}`).join(',')
      })
    };

    if (listing.type === 'roomBlock') {
      return buildPath(eventHandle || '', roomblock, listing.id);
    }

    if (listing.isPickedByCouple) {
      return hotelRoute.accommodationPlace.buildRelativeUrl(eventHandle || '', listing.id || '', paramsData as UtmParameters);
    }
    return hotelRoute.hotelDetails.buildRelativeUrl(eventHandle || '', listing.joyPlaceId || '', paramsData as UtmParameters);
  }, [
    buildPath,
    eventHandle,
    hotelRoute.accommodationPlace,
    hotelRoute.hotelDetails,
    listing.id,
    listing.isPickedByCouple,
    listing.joyPlaceId,
    listing.type,
    roomblock,
    state.occupancies.occupancies,
    state.searchDates.end,
    state.searchDates.start
  ]);

  const redirectUrl = getRedirectUrl();

  return (
    <Container id={`listing-${listing.joyPlaceId}`}>
      <ListingPhotos listing={listing} />

      <ContentPanel>
        <CouplesPick listing={listing} />
        <HeaderBlock listing={listing} venueCoordinates={venueCoordinates} isMobileOrTablet={isMobileOrTablet} />
        {isMobileOrTablet && <Divider marginY={3} />}
        <LogisticsAndCostBlock>
          {!isMobileOrTablet && <TravelLogistics venueCoordinates={venueCoordinates} listing={listing} />}
          <Flex flexDirection={'column'} gap={5}>
            {listing.type === 'roomBlock' && listing.cutoffDate}
            {listing.pricingInfo.isLoading ? (
              <CostBreakdown.Skeleton />
            ) : listing.pricingInfo.values ? (
              <CostBreakdown occupancies={state.occupancies} pricingInfo={listing.pricingInfo.values} noOfNights={noOfNights} />
            ) : null}
            {/* TODO: handle redirect */}
            <HotelCTA joyPlaceId={listing.joyPlaceId || ''} hotelName={listing.name} redirectUrl={redirectUrl} />
          </Flex>
        </LogisticsAndCostBlock>
      </ContentPanel>
    </Container>
  );
};

ListingEntry.Skeleton = ({ isMobileOrTablet }: { isMobileOrTablet: boolean }) => {
  return (
    <Container>
      <ListingPhotos.Skeleton />
      <ContentPanel>
        <HeaderBlock.Skeleton isMobileOrTablet={isMobileOrTablet} />
        {isMobileOrTablet && <Divider marginY={3} />}
        <LogisticsAndCostBlock>
          {!isMobileOrTablet && <TravelLogistics.Skeleton />}
          <CostBreakdown.Skeleton />
        </LogisticsAndCostBlock>
      </ContentPanel>
    </Container>
  );
};

export { ListingEntry };
